<template>
  <div class="vue-address-autocomplete">
    <autocomplete
      ref="autocompleteRef"
      :search="searchAddresses"
      :get-result-value="getResultValue"
      :debounceTime="200"
      :placeholder="props.placeholder"
      :aria-label="props.ariaLabel"
      :required="false"
      @submit="onSelect"
      @blur="onBlur"
      type="text"
      name="address-search"
    />
    <div class="vue-address-autocomplete__spinner" :class="{ loading: showSpinner }">
      <div class="spinner"></div>
    </div>
  </div>
  <div class="validation-text" v-if="showError" v-html="validationText"></div>
</template>

<script setup>
/**
 * This component is used to search for addresses using the WordPress AJAX endpoint
 *
 * To use this component in a Vue application, make sure to include the "VueAddressAutocomplete" component in the surrounding Vue application
 * And render the component by including the '/views/elements/address-autocomplete/address-autocomplete.twig' file
 *
 * See FindRoutesApp.js and find-routes.twig for an example
 */

import { ref, nextTick, onMounted, onBeforeUnmount, watch, computed } from 'vue'
import Autocomplete from 'autocomplete-vue'

// Define emits at the top
const emit = defineEmits(['select', 'error', 'blur'])

// Define props for placeholder and aria-label
const props = defineProps({
  placeholder: {
    type: String,
    default: __('Adresse')
  },
  ariaLabel: {
    type: String,
    default: __('Søg efter adresse')
  },
  required: {
    type: Boolean,
    default: false
  }
})

// Add a ref to the autocomplete component
const autocompleteRef = ref(null)

// Data
const selectedAddress = ref(null);
// const lastSearchStr = ref('');
const lastResults = ref(null);

// Loading state management
const isLoading = ref(false);
const showSpinner = ref(false);
let spinnerTimeout = null;

// Error handling
const showError = ref(false);
const isInvalid = ref(false);

// Computed for validation display
const error = computed(() => selectedAddress.value && selectedAddress.value.errorCode ? selectedAddress.value : null)
const hasError = computed(() => error.value && error.value.errorCode ? true : false)
const validationText = computed(() => error.value && error.value.errorText ? error.value.errorText : '')

// Postcodes that FK doesn't handle (Jysk Fynske Medier)
const postcodeRanges = [
  {
    'name': 'Budnet',
    'postalcodes': ["6880", "6893", "6900", "6940", "6950", "6960", "6971", "6973", "6980", "6990", "7470", "7500", "7560", "7570", "7600", "7620", "7650", "7660", "7673", "7680", "7790", "7830", "7850", "8800", "8830", "8831", "8832", "8840", "8850", "9620", "9631", "9632"],
    'errorText': window.i18n.out_of_range_budnet
  },
  {
    'name': 'Skive',
    'postalcodes': ["7800", "7840", "7860", "7870", "7884"],
    'errorText': window.i18n.out_of_range_skive
  },
  {
    // Postcodes that FK are going to handle in the future
    'name': 'FK',
    //'postalcodes': ["4300", "4305", "4340", "4350", "4360", "4370", "4390", "4420", "4440", "4450", "4500", "4520", "4532", "4534", "4540", "4550", "4560", "4571", "4572", "4573", "4581", "4583"],
    'postalcodes': [],
    'errorText': window.i18n.out_of_range_fk
  }
]

// Cache for search results
const cache = {}

// Helper functions for spinner management
function startSpinnerWithDelay() {
  isLoading.value = true;

  // Clear any existing timeout
  if (spinnerTimeout) {
    clearTimeout(spinnerTimeout);
  }

  // Show spinner after 300ms delay
  spinnerTimeout = setTimeout(() => {
    if (isLoading.value) {
      showSpinner.value = true;
    }
  }, 300);
}

function stopSpinner() {
  isLoading.value = false;
  showSpinner.value = false;

  // Clear the timeout if it hasn't fired yet
  if (spinnerTimeout) {
    clearTimeout(spinnerTimeout);
    spinnerTimeout = null;
  }
}

onMounted(() => {
  autocompleteRef.value.$refs.input.addEventListener('focus', onInputFocus)
  autocompleteRef.value.$refs.input.addEventListener('blur', onInputBlur)
  autocompleteRef.value.$refs.input.addEventListener('input', onInputInput)
})

onBeforeUnmount(() => {
  autocompleteRef.value.$refs.input.removeEventListener('focus', onInputFocus)
  autocompleteRef.value.$refs.input.removeEventListener('blur', onInputBlur)
  autocompleteRef.value.$refs.input.removeEventListener('input', onInputInput)

  // Clean up spinner timeout
  if (spinnerTimeout) {
    clearTimeout(spinnerTimeout)
    spinnerTimeout = null
  }
})

// Set the invalid class on the input field
watch(isInvalid, (newVal) => {
  autocompleteRef.value.$refs.input.classList.toggle('invalid-value', newVal)
})

/**
 * Handle events on the input field
 */
function onInputBlur() {
  // Increment the search counter in the core autocomplete component to avoid that pending results are displayed after focus was moved out of the input field
  autocompleteRef.value.core.searchCounter++;

  // Validate the address if the focus is moved out of the field without searching, or without selecting an address, or has changed the text compared to the selected address
  const searchStr = autocompleteRef.value.$refs.input.value.trim()
  let selected = selectedAddress.value;

  // Don't re-validate if we already have an error
  if (hasError.value) {
    showError.value = true;
    return;
  }

  if (!searchStr && !selected && !props.required) {
    // Reset to the empty state when the field is empty and not required
    setScopeValues(null)
    emit('select', null)
  } else if ((searchStr && !selected) || !searchStr || (searchStr && selected && selected.addressData && searchStr !== selected.addressData.label)) {
    const validatedAddress = validateAddress()
    setScopeValues(validatedAddress)
    //lastSearchStr.value = searchStr
    if (validatedAddress && validatedAddress.errorCode) {
      // Let the parent component know that there is an error
      emit('error', validatedAddress)
    }
  }

  if (!searchStr && selected) {
    // Let the parent component know that no address is selected
    emit('select', null)
  }

  if (hasError.value) {
    showError.value = true;
  }
}

function onInputFocus() {
  isInvalid.value = false;
}

function onInputInput() {
  if (hasError.value && showError.value) {
    // Reset the error
    selectedAddress.value = null;
    showError.value = false;
  }
}

/**
 * Search for addresses using the WordPress AJAX endpoint
 * Based on the logic from the Angular directive
 */
async function searchAddresses(input) {
  if (!input || input.length < 3) {
    stopSpinner()
    return []
  }

  const searchStr = input.trim()

  // Check cache first
  if (searchStr in cache) {
    stopSpinner()
    const cachedResults = cache[searchStr]
    processSearchResults(searchStr, cachedResults)
    return cachedResults
  }

  // Start spinner with delay for non-cached searches
  startSpinnerWithDelay()

  try {
    const searchCounter = autocompleteRef.value.core.searchCounter;

    // Make the AJAX call
    const params = new URLSearchParams({
      'action': 'get_streets_v2',
      'searchstr': searchStr,
    })

    const response = await fetch(window.ajaxurl + '?' + params.toString(), {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP error - status: ${response.status}`)
    }

    const data = await response.json()
    const results = data.streets || []

    // Don't cache the results if an error occurred
    const code = data.status ? data.status.code : undefined;
    if (code != 200) {
      stopSpinner()
      const msg = data.status && data.status.msg ? data.status.msg : __('Der skete en serverfejl. Prøv igen senere.');
      lastResults.value = null;
      // Show the error
      const error = {
        errorCode: 'server-error',
        errorText: msg,
      }
      setScopeValues(error)
      showError.value = true;
      emit('error', error)
      return [];
    }

    // Cache the results
    cache[searchStr] = results

    // Don't process the results if a newer search is in progress
    if (searchCounter === autocompleteRef.value.core.searchCounter) {
      processSearchResults(searchStr, results)
    }

    // Stop loading when search completes successfully
    stopSpinner()

    return results
  } catch (err) {
    stopSpinner()
    console.error('Error fetching addresses:', err)
    return []
  }
}

/**
 * Handle the search results
 */
function processSearchResults(searchStr, results) {
  // Update the last search string
  //lastSearchStr.value = searchStr
  lastResults.value = results

  // Check if there is a match on the address
  checkData(results)
}

/**
 * Check if there is a match on the address, and check if the address is valid
 */
function checkData(results) {
  if (!results) return false;

  const searchstr = autocompleteRef.value.$refs.input.value;
  results.forEach(function (item) {
    if (item.value !== "" && searchstr.replace(",", "").toUpperCase() === item.label.replace(",", "").toUpperCase()) {
      const validatedResult = validateAddress(item, true);
      setScopeValues(validatedResult);
      return;
    }
  });
}

/**
 * Validate the address and add/remove the error class
 * If noInvalidClass is true, the invalid class will not be added
 */
function validateAddress(item, noInvalidClass) {
  const disyValue = item ? item.value : null;
  const addressData = item ? item.object : null;
  const postnr = addressData ? addressData.postnr : null;
  const selskabsnr = addressData ? addressData.selskabsnr : null;
  const address = autocompleteRef.value.$refs.input.value;

  // The result of the validation
  const validated = {
    errorCode: false,
    postnr: postnr,
    addressData: addressData,
    valid: false,
  }

  // validate postcode range and return an error if outside range
  if (postnr && postcodeRanges && postcodeRanges.length > 0) {
    for (const range of postcodeRanges) {
      if (!range.postalcodes || range.postalcodes.length === 0) {
        continue;
      }

      if (range.postalcodes.includes(postnr)) {
        // Return an error response if outside range
        validated.errorCode = 'outside-range';
        validated.name = range.name;
        validated.errorText = range.errorText;
        validated.valid = false;
        // Show the error immediately
        showError.value = true;
        return validated;
      }
    }
  }

  // Check the selskabsnr
  const alt_selskabsnr = window.alt_selskabsnr || null;
  if (alt_selskabsnr && selskabsnr == alt_selskabsnr) {
    // Allow applications when the address belongs to the alternative selskab
  } else if (window.selskabsnr && selskabsnr && window.selskabsnr != selskabsnr) {
    validated.errorCode = 'wrong-selskabsnr';
    if (selskabsnr == '1') {
      validated.errorText = window.i18n.wrong_selskabsnr_fk
    } else {
      validated.errorText = window.i18n.wrong_selskabsnr_nordjyske
    }
    validated.valid = false;
    // Show the error immediately
    showError.value = true;
    return validated;
  }

  // Return success if a full address was selected
  if (disyValue) {
    validated.valid = true;
    return validated;
  }

  // If some results were found, display a message that you must select the address
  if (lastResults.value && lastResults.value.length > 0 && (props.required || address)) {
    validated.errorCode = 'not-selected';
    validated.errorText = __('Du har ikke valgt en adresse');
    validated.valid = false;
    if (!noInvalidClass) {
      isInvalid.value = true;
    }
    return validated;
  }

  // Validate the format of the entered address
  if (address) {
    const isValidAddress = /^(\S+\s*)+\d+.*\d{4}.*$/.test(address);
    if (isValidAddress) {
      validated.errorCode = 'not-found';
      validated.errorText = __('Vi kan ikke finde adressen');
    } else {
      validated.errorCode = 'invalid-address';
      validated.errorText = __('Adressen skal indeholde vejnavn, husnr., postnr. og by.');
    }

    if (!noInvalidClass) {
      isInvalid.value = true;
    }

    validated.valid = false;
    return validated;
  }
}

/**
 * Set scope values
 */
function setScopeValues(result) {
  selectedAddress.value = result;
}

/**
 * Handle selection of an address
 * Emits the selected address data to parent component
 */
function onSelect(result) {
  console.log('Selected result:', result)

  const validatedResult = validateAddress(result, true);
  // Only update the scope value here if the validation is not successful
  if (!validatedResult.valid && validatedResult.errorCode != 'not-selected') {
    setScopeValues(validatedResult);

    // Emit an "error" event to the parent component in case of an error
    emit('error', validatedResult)
    return
  }

  // If we don't have a complete address, search again
  if (!result.id) {
    // Perform a new search by sending an "input" event to the autocomplete component
    nextTick(() => {
      autocompleteRef.value.$refs.input.dispatchEvent(new Event('input'))
    })
    return
  }

  setScopeValues(validatedResult);

  // Emit the selected result to the parent component
  console.log('Selected address:', result)
  emit('select', validatedResult)
}

/**
 * Emit an event when the user leaves the address field
 * This is used on the application form to validate the address field
 */
function onBlur() {
  emit('blur', selectedAddress.value)
}

/**
 * Helper function for getting the label that should be displayed in the input field.
 */
function getResultValue(result) {
  return result.label
}

// Expose the internal refs
defineExpose({
  autocompleteRef,
})

</script>

<style lang="scss">
@use 'common' as *;

.vue-address-autocomplete {
  position: relative;

  input {
    outline: none;
  }

  &__spinner {
    position: absolute;
    top: 50%;
    right: 13px;
    transform: translateY(-50%);
    opacity: 0;

    body.loaded & {
      // Don't set the transition until the page has loaded to avoid the spinner being shortly visible after page load (only relevant when doing development with hot reloading)
      transition: opacity 0.3s ease-in 0.1s;
    }

    .spinner {
      width: 24px;
      height: 24px;
      border-width: 3px;
    }

    &.loading {
      opacity: 1;
      transition: opacity 0.3s ease-out;
    }
  }

  [data-position="above"] .autocomplete-input[aria-expanded="true"] {
    border-top-color: transparent;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  [data-position="below"] .autocomplete-input[aria-expanded="true"] {
    border-bottom-color: transparent;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .autocomplete-result-list {
    margin: 0;
    border: 1px solid var(--border-color);
    padding: 0;
    box-sizing: border-box;
    max-height: 378px;
    overflow-y: auto;
    background: var(--color-white);
    list-style: none;
    z-index: z(above-footer) !important;
  }

  [data-position="above"] .autocomplete-result-list {
    margin-bottom: -1px;
    border-radius: var(--border-radius) var(--border-radius) 0 0;
  }

  [data-position="below"] .autocomplete-result-list {
    margin-top: -1px;
    border-radius: 0 0 var(--border-radius) var(--border-radius);
  }

  /* Single result item */
  .autocomplete-result {
    cursor: default;
    padding: 8px 16px;

    &:not(:last-child) {
      border-bottom: 1px solid var(--color-grey);
    }
  }

  .autocomplete-result:hover,
  .autocomplete-result[aria-selected="true"] {
    background-color: rgba(0, 0, 0, 0.06);
  }
}
</style>
